import {NewsItem} from '../domain/news-item';
import {parse, X2jOptions} from 'fast-xml-parser';
import * as he from 'he';
import {PoaTranslatedString} from '../domain/poa-translated-string';
import {PoaLanguage} from '../domain/poa-language';
import { ParsedNewsMl } from '../domain/parsed-news-ml';
import { ParsedMultiLanguageAttribute } from '../domain/parsed-multi-language-attribute';
import { ParsedMultiLanguageNameAttribute } from '../domain/parsed-multi-language-name-attribute';
import { ParsedTranslation } from '../domain/parsed-translation';

export class NewsItemHelpers {

    static getMessages(newsMlMessage: string): NewsItem[] {
        const returnValue: NewsItem[] = new Array<NewsItem>();

        const options: Partial<X2jOptions> = {
            attributeNamePrefix : '',
            textNodeName : 'content',
            ignoreAttributes : false,
            ignoreNameSpace : true,
            allowBooleanAttributes : false,
            parseNodeValue : true,
            parseAttributeValue : false,
            trimValues: true,
            attrValueProcessor: val => he.decode(val, {isAttributeValue: true}),
            tagValueProcessor: val => he.decode(val),
            arrayMode: true
        };

        const parsedNewsML = parse(newsMlMessage, options) as ParsedNewsMl;

        if (parsedNewsML.newsMessage &&
            parsedNewsML.newsMessage[0].itemSet &&
            parsedNewsML.newsMessage[0].itemSet[0].newsItem) {
            parsedNewsML.newsMessage[0].itemSet[0].newsItem.forEach(element => {
                const newsItem: Partial<NewsItem> = {
                    guid: element.guid
                };

                // itemMeta
                if (element.itemMeta[0]) {
                    const subElement = element.itemMeta[0];
                    if (subElement.versionCreated) {
                        newsItem.versionCreated = new Date(subElement.versionCreated);
                    }
                    if (subElement.provider) {
                        newsItem.provider = this.getMultilanguageNameAttribute(subElement.provider);
                    }
                    if (subElement.link) {
                        newsItem.link = this.getMultilanguageAttribute(subElement.link, 'href');
                    }
                }

                // contentMeta
                if (element.contentMeta) {
                    const subElement = element.contentMeta[0];
                    if (subElement.altId) {
                        newsItem.altId = subElement.altId[0].content;
                    }
                    if (subElement.contentCreated) {
                        newsItem.contentCreated = new Date(subElement.contentCreated);
                    }
                    if (subElement.creator) {
                        newsItem.creator = this.getMultilanguageNameAttribute(subElement.creator);
                    }
                    if (subElement.headline) {
                        newsItem.headline = this.getMultilanguageAttribute(subElement.headline, 'content');

                        // Sets the language the summary table uses to display multilanguage fields
                        if (newsItem.headline.de) {
                            newsItem.displayLanguageSummary = 'de';
                        } else if (newsItem.headline.fr) {
                            newsItem.displayLanguageSummary = 'fr';
                        } else if (newsItem.headline.it) {
                            newsItem.displayLanguageSummary = 'it';
                        } else {
                            newsItem.displayLanguageSummary = 'en';
                        }

                        // languages in newsItem
                        const newsItemLanguages: NewsItem['languages'] = [];
                        newsItem.languages = newsItemLanguages;
                        subElement.headline.forEach(item => {
                            newsItemLanguages.push(item.lang);
                        });
                    }
                    if (subElement.located) {
                        newsItem.located = this.getMultilanguageNameAttribute(subElement.located);

                        if (subElement.located[0].broader) {
                            const broader: string[] = new Array<string>();
                            subElement.located[0].broader.forEach(item => {
                               if (item.type === 'cptype:statprov') {
                                   broader.push(item.name);
                               }
                            });
                            newsItem.broader = broader.join(', ');
                        }
                    }
                    if (subElement.subject) {
                        subElement.subject.forEach(subject => {
                            if (subject.qcode === 'medtop:03000000') {
                                newsItem.eventType = this.getMultilanguageNameAttribute([subject]);
                            }
                            if (subject.qcode === 'poa:expireTime') {
                                newsItem.expireTime = new Date(subject.name);
                            }
                            if (subject.qcode === 'poa:onset') {
                                newsItem.onset = new Date(subject.name);
                            }
                            if (subject.qcode === 'poa:readoutLanguages') {
                                if (Array.isArray(subject.name)) {
                                    newsItem.readoutLanguages = subject.name.join(', ');
                                } else {
                                    newsItem.readoutLanguages = subject.name;
                                }

                            }
                            if (subject.qcode === 'poa:dutyToPublish') {
                                newsItem.dutyToPublish = true;
                            }
                        });
                    }

                    if (subElement.urgency) {
                        newsItem.urgency = subElement.urgency;
                    }
                }

                // contentSet
                if (element.contentSet) {
                    if (element.contentSet[0].inlineXML[0].poaContent[0].poaLanguageContent) {

                        const newsItemMessage: NewsItem['message'] = {};
                        newsItem.message = newsItemMessage;
                        element.contentSet[0].inlineXML[0].poaContent[0].poaLanguageContent.forEach(messageContent => {
                            const language: string = messageContent.lang;
                            const message: string[] = new Array<string>();

                            if (messageContent.poaCreator) {
                                this.addMessageElement(messageContent.poaCreator, message);
                            }

                            if (messageContent.poaDescription) {
                                this.addMessageElement(messageContent.poaDescription, message);
                            }

                            if (messageContent.poaEffect) {
                                this.addMessageElement(messageContent.poaEffect, message);
                            }

                            if (messageContent.poaBehaviorInstructions) {
                                this.addMessageElement(messageContent.poaBehaviorInstructions, message);
                            }

                            if (messageContent.poaContactLink) {
                                this.addMessageElement(messageContent.poaContactLink, message);
                            }

                            if (messageContent.poaContactPhone) {
                                this.addMessageElement(messageContent.poaContactPhone, message);
                            }

                            switch (language) {
                                case 'de':
                                    newsItemMessage.de = message.join('\n');
                                    break;
                                case 'fr':
                                    newsItemMessage.fr = message.join('\n');
                                    break;
                                case 'it':
                                    newsItemMessage.it = message.join('\n');
                                    break;
                                case 'en':
                                    newsItemMessage.en = message.join('\n');
                                    break;
                                default:
                                    console.warn('unexpected lang in poaMessage:', language);
                            }
                        });
                    }
                }

                returnValue.push(newsItem as NewsItem);
            });
        } else {
            console.log('getMessages: no newsItem in received newsMLmessage');
        }

        return returnValue;
    }

    private static addMessageElement(messageElement: ParsedTranslation, message: string[]): void {
        if (Array.isArray(messageElement)) {
            messageElement.forEach(item => {
                if (typeof item === 'object' && 'translatedBy' in item) {
                    message.push(item.content);
                } else {
                    message.push(item);
                }
            });
        } else {
            message.push(messageElement);
        }
    }

    private static getMultilanguageNameAttribute(
        attribute: ParsedMultiLanguageNameAttribute[]
    ): PoaTranslatedString {
        const returnValue: PoaTranslatedString = {};
        attribute[0].name.forEach(element => {
            switch (element.lang) {
                case 'de':
                    returnValue.de = element.content;
                    break;
                case 'fr':
                    returnValue.fr = element.content;
                    break;
                case 'it':
                    returnValue.it = element.content;
                    break;
                case 'en':
                    returnValue.en = element.content;
                    break;
                default:
                    console.warn('unexpected lang in name attribute:', element);
            }
        });
        return returnValue;
    }

    private static getMultilanguageAttribute<ParameterName extends keyof Record<string, unknown>>(
        attribute: ParsedMultiLanguageAttribute<ParameterName>[],
        parameterName: ParameterName
    ): PoaTranslatedString {
        const returnValue: PoaTranslatedString = {};
        attribute.forEach(element => {
            switch (element.lang) {
                case 'de':
                    returnValue.de = element[parameterName];
                    break;
                case 'fr':
                    returnValue.fr = element[parameterName];
                    break;
                case 'it':
                    returnValue.it = element[parameterName];
                    break;
                case 'en':
                    returnValue.en = element[parameterName];
                    break;
                default:
                    console.warn('unexpected lang in attribute:', element);
            }
        });
        return returnValue;
    }
}
